import {
  Divider,
  Box,
  Checkbox,
  CircularProgress,
  Radio,
  Typography,
  useTheme,
} from "@mui/material";
import PaginatorCst from "@components/PaginatorCst";
import SearchBar from "./SearchBar";
import { SearchableMultiSelectListProps } from "./types";

function SearchableMultiSelectListCst<T>({
  selectionMode = "multi",
  rowVariant = "boxed",
  showSelectedCount = true,
  items,
  getId,
  renderRow,
  selectedIds,
  onToggle,
  initialSearchValue,
  onSearchSubmit,
  searchPlaceholder,
  searchButtonLabel,
  hideSearch = false,
  filtersSlot,
  headerSlot,
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  isLoading,
  selectedCountLabel,
  selectedCountEndSlot,
  emptyLabel,
}: SearchableMultiSelectListProps<T>) {
  const theme = useTheme();
  const isSingle = selectionMode === "single";
  const isBare = rowVariant === "bare";

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {!hideSearch && (
        <SearchBar
          initialValue={initialSearchValue}
          onSubmit={onSearchSubmit}
          placeholder={searchPlaceholder}
          buttonLabel={searchButtonLabel}
          filtersSlot={filtersSlot}
        />
      )}

      {headerSlot}

      {(showSelectedCount || selectedCountEndSlot) && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          {showSelectedCount ? (
            <Typography
              sx={{ fontSize: "13px", color: theme.palette.text.tertiary }}
            >
              {selectedCountLabel}
            </Typography>
          ) : (
            <Box />
          )}
          {selectedCountEndSlot}
        </Box>
      )}

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : items.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 6,
            color: theme.palette.text.tertiary,
            fontSize: "13px",
          }}
        >
          {emptyLabel}
        </Box>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {items.map((item) => {
            const id = getId(item);
            const selected = selectedIds.has(id);
            const handleToggle = (next: boolean) => onToggle(id, next);
            return (
              <Box
                key={String(id)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  ...(isBare
                    ? {}
                    : {
                        padding: "12px 16px",
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                      }),
                }}
              >
                {isSingle ? (
                  <Radio
                    checked={selected}
                    onChange={(_, next) => handleToggle(next)}
                    sx={{ p: 0 }}
                  />
                ) : (
                  <Checkbox
                    checked={selected}
                    onChange={(_, next) => handleToggle(next)}
                    sx={{ p: 0 }}
                  />
                )}
                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{ borderColor: theme.palette.divider }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {renderRow({ item, selected, onToggle: handleToggle })}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {totalPages > 1 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <PaginatorCst
            page={page}
            pageSize={pageSize}
            totalItems={totalItems}
            onPageChange={onPageChange}
          />
        </Box>
      )}
    </Box>
  );
}

export default SearchableMultiSelectListCst;
